<template>
  <div v-if="visible" class="toast-message" :style="{ borderColor: computedColor }">
    <div class="toast-icon" :style="{ background: computedColor }">
      <img :src="icon" alt="icon" class="check-svg" />
    </div>
    <div class="toast-content">
      <div class="toast-title">{{ message }}</div>
      <div class="toast-submessage">{{ submessage }}</div>
    </div>
    <button class="toast-close" @click="closeToast" aria-label="Cerrar">
      <svg
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path d="M18 6L6 18" stroke="#333" stroke-width="2" stroke-linecap="round" />
        <path d="M6 6L18 18" stroke="#333" stroke-width="2" stroke-linecap="round" />
      </svg>
    </button>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, defineEmits, onUnmounted, computed } from 'vue'

const props = defineProps({
  message: { type: String, required: true },
  submessage: { type: String, default: '' },
  duration: { type: Number, default: 3000 },
  modelValue: { type: Boolean, default: true },
  type: { type: String as () => 'success' | 'error', default: 'success' },
  icon: { type: String, default: '/src/assets/check-icon.svg' },
})

const computedColor = computed(() => {
  return props.type === 'success' ? '#9CD41C' : '#D0342C'
})
const emit = defineEmits(['update:modelValue', 'close'])
const visible = ref(props.modelValue)
let timeoutId: ReturnType<typeof setTimeout> | null = null

const closeToast = () => {
  visible.value = false
  emit('update:modelValue', false)
  emit('close')
}

const startTimeout = () => {
  if (timeoutId) {
    clearTimeout(timeoutId)
  }
  timeoutId = setTimeout(() => {
    closeToast()
  }, props.duration)
}

watch(
  () => props.modelValue,
  (val) => {
    visible.value = val
    if (val) {
      startTimeout()
    }
  },
  { immediate: true },
)

onUnmounted(() => {
  if (timeoutId) {
    clearTimeout(timeoutId)
  }
})
</script>

<style scoped>
.toast-message {
  box-sizing: border-box;
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 0 0 0 0;
  gap: 16px;
  isolation: isolate;
  position: fixed;
  width: 354px;
  height: 79px;
  left: calc(50% - 354px / 2 - 6px);
  bottom: 44px;
  background: #fff;
  border: 2px solid #9cd41c;
  border-radius: 16px;
  z-index: 10001;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
}
.toast-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 79px;
  height: 100%;
  background: #9cd41c;
  border-top-left-radius: 14px;
  border-bottom-left-radius: 14px;
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
  margin: 0;
  padding: 0;
  flex-shrink: 0;
}
.check-svg {
  width: 34px;
  height: 35px;
}
.toast-content {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  justify-content: center;
  align-items: flex-start;
  gap: 0;
}
.toast-title {
  font-weight: 700;
  font-size: 1rem;
  color: #222;
  margin-bottom: 0px;
  line-height: 1.2;
  text-align: left;
}
.toast-submessage {
  font-weight: 400;
  font-size: 0.95rem;
  color: #222;
  margin-top: 2px;
  line-height: 1.2;
  text-align: left;
}
.toast-close {
  background: transparent;
  border: none;
  cursor: pointer;
  margin-right: 16px;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
}
.toast-close svg {
  display: block;
}
@media (max-width: 400px) {
  .toast-message {
    width: 98vw;
    left: 1vw;
    min-width: 0;
  }
}
</style>
